import React, { useCallback, useEffect, useState } from "react";

import { apiGet, apiPost } from "../../../../Api/Client.js";
import { errorText } from "../../../../Shared/uiStyles.js";
import { Alert, Badge, Button, EmptyState, Field, FormGrid, Input, Section, Select, Table } from "../../../../Ui/index.jsx";
import { entryKindsFor, payTypeLabel } from "./formulaPayTypes.js";

const TONE = { approved: "success", draft: "warning", void: "neutral" };

// The days, hours and amounts a month's pay is worked out from. Someone enters them; a second person approves.
// `people` are the month's pay rows (each knows the person's pay type); `onChanged` reloads the month.
export function PayInputsPanel({ month, year, people, canPrepare, onChanged }) {
  const [list, setList] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ employee: "", kind: "", quantity: "", note: "" });

  const load = useCallback(() => apiGet(`/FinanceAndPayroll/pay-inputs/?month=${month}&year=${year}`)
    .then((out) => { setList(out); setError(""); })
    .catch((err) => setError(errorText(err, "These could not be loaded."))), [month, year]);
  useEffect(() => { setList(null); load(); }, [load]);

  const paid = people.filter((p) => entryKindsFor(p.pay_type).length > 0);
  const chosen = paid.find((p) => String(p.employee_id) === String(form.employee));
  const kinds = chosen ? entryKindsFor(chosen.pay_type) : [];

  const run = async (call) => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await call();
      await load();
      if (onChanged) await onChanged();
      return true;
    } catch (err) {
      setError(errorText(err, "That did not work. Please try again."));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const add = async (event) => {
    event.preventDefault();
    const ok = await run(() => apiPost("/FinanceAndPayroll/pay-inputs/", { employee: Number(form.employee), month, year, kind: form.kind, quantity: form.quantity, note: form.note }));
    if (ok) setForm({ employee: form.employee, kind: form.kind, quantity: "", note: "" });
  };

  const columns = [
    { key: "name", header: "Name", className: "is-card-title", render: (r) => <span className="ds-strong">{r.name}</span> },
    { key: "kind", header: "What", render: (r) => r.kind_label },
    { key: "quantity", header: "Entered", align: "right", render: (r) => Number(r.quantity).toLocaleString("en-IN") },
    { key: "status", header: "Status", render: (r) => <Badge variant={TONE[r.status] || "neutral"}>{r.status_label}</Badge> },
    { key: "actions", header: "", align: "right", className: "is-card-actions", render: (r) => (canPrepare && r.status !== "void" ? (
      <div className="Pay-Actions">
        {r.status === "draft" && <Button size="sm" variant="primary" disabled={busy} onClick={() => run(() => apiPost(`/FinanceAndPayroll/pay-inputs/${r.id}/approve/`, {}))}>Approve</Button>}
        <Button size="sm" disabled={busy} onClick={() => run(() => apiPost(`/FinanceAndPayroll/pay-inputs/${r.id}/cancel/`, {}))}>Take back</Button>
      </div>
    ) : null) },
  ];

  if (paid.length === 0 && !(list?.inputs || []).length) return null;
  return (
    <Section title="Days, hours and amounts">
      {error && <Alert tone="danger" role="alert">{error}</Alert>}
      {list === null && !error ? <EmptyState>Loading…</EmptyState> : (
        <Table stack columns={columns} rows={list?.inputs || []} empty="Nothing entered for this month yet." />
      )}
      {canPrepare && paid.length > 0 && (
        <form className="Pay-Form" onSubmit={add}>
          <FormGrid>
            <Field label="Person">
              <Select value={form.employee} placeholder="Choose a person"
                onChange={(e) => setForm({ employee: e.target.value, kind: "", quantity: "", note: "" })}
                options={paid.map((p) => ({ value: p.employee_id, label: `${p.name} · ${payTypeLabel(p.pay_type)}` }))} />
            </Field>
            <Field label="What">
              <Select value={form.kind} placeholder="Choose" disabled={!chosen} onChange={(e) => setForm({ ...form, kind: e.target.value })}
                options={kinds.map(([value, label]) => ({ value, label }))} />
            </Field>
            <Field label="Number"><Input inputMode="decimal" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} required /></Field>
            <Field label="Note (optional)"><Input value={form.note} maxLength={300} onChange={(e) => setForm({ ...form, note: e.target.value })} /></Field>
          </FormGrid>
          <div className="Modal-Actions">
            <Button type="submit" variant="primary" disabled={busy || !form.employee || !form.kind || !form.quantity}>Add</Button>
          </div>
        </form>
      )}
    </Section>
  );
}
